import React, { useState } from 'react';
import api from 'services/api';
import { 
  Play, CheckCircle, XCircle, AlertCircle, 
  Loader, Code, Database, Wifi 
} from 'lucide-react';

const TesteN8n = () => {
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTest, setActiveTest] = useState(null);

  const tests = [
    {
      id: 'conexao',
      name: 'Teste de Conexão',
      description: 'Verifica se a API está respondendo',
      icon: Wifi,
      action: () => api.get('/test/webhook-connection')
    },
    {
      id: 'contratos_mov',
      name: 'Buscar Contratos MOV Fibra',
      description: 'Testa busca de contratos da MOV Fibra',
      icon: Database,
      action: () => api.get('/contratos', { params: { empresa: 'MOV Fibra' } })
    },
    {
      id: 'contratos_xt',
      name: 'Buscar Contratos XT Fibra',
      description: 'Testa busca de contratos da XT Fibra',
      icon: Database,
      action: () => api.get('/contratos', { params: { empresa: 'XT Fibra' } })
    },
    {
      id: 'cidades',
      name: 'Buscar Cidades',
      description: 'Testa busca de cidades disponíveis',
      icon: Code,
      action: () => api.get('/comercial/cidades')
    },
    {
      id: 'vendedores',
      name: 'Buscar Vendedores',
      description: 'Testa busca de vendedores',
      icon: Code,
      action: () => api.get('/comercial/vendedores')
    }
  ];

  const runTest = async (test) => {
    setActiveTest(test.id);
    setLoading(true);

    try {
      const startTime = Date.now();
      const result = await test.action();
      const endTime = Date.now();
      const duration = endTime - startTime;

      setTestResults(prev => ({
        ...prev,
        [test.id]: {
          success: result.success,
          data: result,
          duration,
          timestamp: new Date().toISOString()
        }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [test.id]: {
          success: false,
          error: error.message,
          duration: 0,
          timestamp: new Date().toISOString()
        }
      }));
    } finally {
      setLoading(false);
      setActiveTest(null);
    }
  };

  const runAllTests = async () => {
    setLoading(true);
    for (const test of tests) {
      await runTest(test);
      // Pequena pausa entre os testes
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    setLoading(false);
  };

  const getStatusIcon = (testId) => {
    const result = testResults[testId];
    if (activeTest === testId) {
      return <Loader className="h-5 w-5 text-blue-500 animate-spin" />;
    }
    if (!result) {
      return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
    return result.success 
      ? <CheckCircle className="h-5 w-5 text-green-500" />
      : <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusColor = (testId) => {
    const result = testResults[testId];
    if (activeTest === testId) return 'border-blue-200 bg-blue-50';
    if (!result) return 'border-gray-200 bg-white';
    return result.success 
      ? 'border-green-200 bg-green-50' 
      : 'border-red-200 bg-red-50';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Teste de Integração n8n
            </h1>
            <p className="text-gray-600">
              Verificar conexão com: <code className="bg-gray-100 px-2 py-1 rounded text-sm">https://n8n.mov.pro.br/webhook-test/sitemov</code>
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-800">⚠️ Webhook de Teste</h3>
                <p className="text-yellow-700 text-sm mt-1">
                  Este é um webhook de teste. Você precisa:
                </p>
                <ol className="text-yellow-700 text-sm mt-2 list-decimal list-inside space-y-1">
                  <li>Acessar o n8n e abrir o workflow</li>
                  <li><strong>Clicar em "Test workflow"</strong> no canvas</li>
                  <li>Executar os testes <strong>IMEDIATAMENTE</strong> após clicar</li>
                  <li>O webhook funciona apenas para <strong>uma chamada</strong> por teste</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="flex justify-center mb-8">
            <button
              onClick={runAllTests}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors"
            >
              <Play className="h-5 w-5" />
              <span>{loading ? 'Executando Testes...' : 'Executar Todos os Testes'}</span>
            </button>
          </div>

          <div className="grid gap-4">
            {tests.map((test) => {
              const result = testResults[test.id];
              const IconComponent = test.icon;

              return (
                <div
                  key={test.id}
                  className={`border rounded-xl p-6 transition-all duration-300 ${getStatusColor(test.id)}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <IconComponent className="h-6 w-6 text-purple-600" />
                      <div>
                        <h3 className="font-semibold text-gray-900">{test.name}</h3>
                        <p className="text-sm text-gray-600">{test.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {result && (
                        <span className="text-xs text-gray-500">
                          {result.duration}ms
                        </span>
                      )}
                      {getStatusIcon(test.id)}
                      <button
                        onClick={() => runTest(test)}
                        disabled={loading}
                        className="bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 px-3 py-1 rounded text-sm font-medium transition-colors"
                      >
                        Testar
                      </button>
                    </div>
                  </div>

                  {result && (
                    <div className="mt-4 p-4 bg-white rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Resultado:</span>
                        <span className="text-xs text-gray-500">
                          {new Date(result.timestamp).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      
                      {result.success ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-green-700 font-medium">Sucesso</span>
                          </div>
                          
                          {result.data.data && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Dados retornados:</p>
                              <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                                {JSON.stringify(result.data.data, null, 2)}
                              </pre>
                            </div>
                          )}
                          
                          {result.data.message && (
                            <p className="text-sm text-gray-700">
                              <strong>Mensagem:</strong> {result.data.message}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <XCircle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-700 font-medium">Erro</span>
                          </div>
                          
                          <p className="text-sm text-red-600">
                            {result.error || result.data?.message || 'Erro desconhecido'}
                          </p>
                          
                          {result.data?.error && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Detalhes do erro:</p>
                              <pre className="text-xs bg-red-50 p-2 rounded overflow-x-auto text-red-700">
                                {JSON.stringify(result.data.error, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">Informações da Integração</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Endpoint:</strong> https://n8n.mov.pro.br/webhook-test/sitemov</p>
              <p><strong>Método:</strong> POST</p>
              <p><strong>Timeout:</strong> 30 segundos</p>
              <p><strong>Headers:</strong> Content-Type: application/json</p>
              <p><strong>Modo:</strong> Teste (requer ativação manual)</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-900 mb-2">Estrutura Esperada do n8n</h4>
            <pre className="text-xs text-yellow-800 overflow-x-auto">
{`// Exemplo de requisição para buscar contratos:
{
  "action": "buscar_contratos",
  "data_inicio": "2025-01-01",
  "data_fim": "2025-06-30",
  "empresa": "MOV Fibra",
  "status": "todos",
  "cidade": "todas"
}

// Exemplo de resposta esperada:
{
  "success": true,
  "contratos": [...],
  "resumo": {
    "totalContratos": 10,
    "valorTotal": 1000.00,
    "ticketMedio": 100.00,
    "ativos": 8,
    "cidadesUnicas": 3,
    "percentualAtivos": "80.0"
  }
}`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TesteN8n; 

